import {
  useActiveCustomer,
  useCustomerGeneral,
  useUpdateCustomer,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerFormValues,
  CustomerGeneral,
} from '@/core/domain/people/Customers/Customers.types';
import { removeMask } from '@/utils/mask';
import { DateTimeFormat } from '@/utils/moment';
import { Form } from 'antd';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';

type Props = PropsWithChildren<unknown>;
type Forms = {
  customerGeneral?: CustomerFormValues;
};

type CustomerFormContextProps = {
  isLoading: boolean;
  forms: Forms;
  customerId: string;
  disabled: boolean;
  activeCustomer: boolean;
  setActiveCustomer: (isActive: boolean) => void;
  handleActive: () => void;
  loadActive: boolean;
  setLoadActive: (isActive: boolean) => void;
  handleSubmit: () => void;
  customer?: CustomerGeneral;
  customerDetailsLoading: boolean;
};

const CustomerFormContext = createContext({} as CustomerFormContextProps);
export const useCustomerFormContext = () => useContext(CustomerFormContext);

const CustomerFormProvider: React.FC<Props> = ({ children }) => {
  const { params } = useRouteMatch<{ customerId: string }>();

  const [forms, setForms] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [customerId, setCustomerId] = useState<string>('');
  const [activeCustomer, setActiveCustomer] = useState<boolean>(true);
  const [loadActive, setLoadActive] = useState<boolean>(true);

  const customerGeneralUpdate = useUpdateCustomer();
  const activeCustomerHook = useActiveCustomer();
  const { data: customer, isLoading: isFetching } = useCustomerGeneral(
    Number(customerId),
  );

  const disabled = !Object.keys(forms).some((key) => !!key);

  useEffect(() => {
    setForms({});
    if (params.customerId) {
      setCustomerId(params.customerId);
    }
  }, [params.customerId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setForms((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    setIsLoading(true);

    try {
      if (forms.customerGeneral && typeof customerId === 'string') {
        const { recommendation, ...values } = forms.customerGeneral;
        const obj = recommendation?.id
          ? { ...values, recommendation }
          : { ...values };

        await customerGeneralUpdate
          .mutateAsync({
            id: Number(customerId),
            obj: {
              ...obj,
              birthDate: values.birthDate?.format(DateTimeFormat),
              creation: values.creation?.format(DateTimeFormat),
              active: activeCustomer,
              cpfCnpj: values.cpfCnpj ? removeMask(values.cpfCnpj) : undefined,
            },
          })
          .then(() => setForms({}))
          .then(() => setIsLoading(false));
      }
    } catch (error: unknown) {
      setIsLoading(false);
    }
  }

  async function handleActive() {
    setLoadActive(true);
    if (customerId) {
      try {
        await activeCustomerHook
          .mutateAsync(Number(customerId))
          .then(() => setActiveCustomer(!activeCustomer))
          .then(() => setLoadActive(false));
      } catch {}
    }
  }

  return (
    <CustomerFormContext.Provider
      value={{
        isLoading,
        forms,
        customerId,
        disabled,
        activeCustomer,
        setActiveCustomer,
        handleActive,
        loadActive,
        setLoadActive,
        handleSubmit,
        customer,
        customerDetailsLoading: isFetching,
      }}
    >
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={handleFormChange}
      >
        {children}
      </Form.Provider>
    </CustomerFormContext.Provider>
  );
};

export default CustomerFormProvider;
